import PaneContainer from "@/layouts/components/PaneContainer"
import SeparablePanel from "@/components/SeparablePanel"
import TwoColumn from "@/layouts/TwoColumn"

import ItemTreeTable from "./components/ItemTreeTable"
import ItemDetail from "./components/ItemDetail"
import CategoryDetail from "./components/categoryDetail"
import DateSelector from "./components/DateSelector"
import DateSummary from "./components/DateSummary"
import { Dialog } from "primereact/dialog"
import { Button } from "primereact/button"
import { Toast } from "primereact/toast"
import { Message } from "primereact/message"
import { ProgressBar } from "primereact/progressbar"
import Optimizer from "../CreatePlan/steps/Optimizer"
import FactoryChart from "./components/charts/FactoryChart"
import OptimizationOverviewPanel from "./components/OptimizationOverviewPanel"
import PlanActionBar from "./components/PlanActionBar"
import XMLExportDialog from "./components/XMLExportDialog"
import { usePlanPage } from "./hooks/usePlanPage"

const Plan = () => {
  const {
    toastRef,
    items,
    availableDates,
    excelPlanResponse,
    currentResponse,
    itemsStatus,
    itemStats,
    inventories,
    shipments,
    productions,
    excelProductions,
    optimizerProductions,
    numericOptimizerProductions,
    numericProductions,
    selectedItems,
    isCategory,
    selectedDate,
    handleSelectionChange,
    setSelectedDate,
    handleProductionChange,
    userProductionChanges,
    optimizerVisible,
    previewOptimizeRequest,
    optimizeQueryData,
    handleOptimize,
    handleOptimizeDialogClose,
    xmlModalVisible,
    setXmlModalVisible,
    xmlResponse,
    setUserProductionChanges,
    actionBarConfig,
    excelReloadDialog,
    excelWriteDialog,
    optimizationLogs,
  } = usePlanPage()

  return (
    <>
      <TwoColumn
        header={<PlanActionBar {...actionBarConfig} />}
        leftPane={(
          <PaneContainer>
            <ItemTreeTable
              items={items}
              itemStatus={itemsStatus}
              itemStats={itemStats}
              onSelectionChange={handleSelectionChange}
            />
            <FactoryChart
              items={items}
              日付={currentResponse?.dates ?? []}
              productions={numericOptimizerProductions}
              onDateClick={setSelectedDate}
            />
            <SeparablePanel toggleable collapsed header="最適化サマリー">
              <OptimizationOverviewPanel
                logs={optimizationLogs}
              />
            </SeparablePanel>
            <DateSelector
              dates={availableDates}
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
              holidayDates={excelPlanResponse?.holidayDates}
            />
          </PaneContainer>
        )}
        rightPane={(
          <PaneContainer>
            {selectedDate ? (
              <DateSummary
                selectedDate={selectedDate}
                items={items}
                dates={currentResponse?.dates ?? []}
                inventories={inventories}
                shipments={shipments}
                productions={numericProductions}
                holidayDates={excelPlanResponse?.holidayDates}
              />
            ) : isCategory ? (
              <CategoryDetail
                items={selectedItems}
                itemStats={itemStats}
                inventories={inventories}
                shipments={shipments}
                productions={numericProductions}
                dates={currentResponse?.dates ?? []}
              />
            ) : (
              <ItemDetail
                items={selectedItems}
                itemStats={itemStats}
                inventories={inventories}
                shipments={shipments}
                productions={productions}
                dates={currentResponse?.dates ?? []}
                onProductionChange={handleProductionChange}
                excelProductions={excelProductions}
                optimizerProductions={optimizerProductions}
                userProductions={userProductionChanges}
                logs={optimizationLogs}
              />
            )}
          </PaneContainer>
        )}
      />

      <Dialog header="最適化実行" visible={optimizerVisible} style={{ width: '50vw' }} onHide={handleOptimizeDialogClose}>
        {optimizeQueryData && previewOptimizeRequest && (
          <Optimizer
            originalResponse={optimizeQueryData}
            optimizeRequest={previewOptimizeRequest}
            onBack={handleOptimizeDialogClose}
            onProceed={handleOptimize}
          />
        )}
      </Dialog>

      <Dialog
        header="OneDrive再読み込み"
        visible={excelReloadDialog.visible}
        style={{ width: '32rem' }}
        closable={!excelReloadDialog.isExcelReloading}
        onHide={excelReloadDialog.close}
        footer={<Button label="閉じる" onClick={excelReloadDialog.close} disabled={excelReloadDialog.isExcelReloading} />}
      >
        <ul className="list-none m-0 p-0 flex flex-col gap-3">
          {excelReloadDialog.progress.map((step) => {
            const iconClass = {
              pending: 'pi pi-clock text-gray-400',
              active: 'pi pi-spin pi-spinner text-blue-500',
              completed: 'pi pi-check-circle text-green-500',
              error: 'pi pi-times-circle text-red-500',
            }[step.status]
            return (
              <li key={step.id} className="flex items-center gap-3">
                <div className="mt-2">
                  <i className={iconClass} style={{ fontSize: '1.25rem' }} />
                </div>
                <div>
                  <p className="m-0 font-medium">{step.label}</p>
                  {step.status === 'active' && <small className="text-gray-500">処理中...</small>}
                  {step.status === 'error' && <small className="text-red-500">エラーが発生しました</small>}
                </div>
              </li>
            )
          })}
        </ul>
        {excelReloadDialog.stage === 'success' && (
          <p className="text-green-600 mt-4 mb-0">最新データの取り込みが完了しました。</p>
        )}
        {excelReloadDialog.error && (
          <p className="text-red-500 mt-4 mb-0">{excelReloadDialog.error}</p>
        )}
      </Dialog>

      <Dialog
        header="Excelファイルに保存"
        visible={excelWriteDialog.visible}
        style={{ width: '32rem' }}
        closable={!excelWriteDialog.isWriting}
        onHide={excelWriteDialog.close}
        footer={<Button label="閉じる" onClick={excelWriteDialog.close} disabled={excelWriteDialog.isWriting} />}
      >
        <p className="text-sm text-600">
          Excelを開いたままだと書き込みに失敗します。ファイルを閉じてから実行してください。
        </p>
        {excelWriteDialog.isLocked && (
          <>
            <Message
              severity="warn"
              text="ファイルが他のアプリで開かれています。Excelを閉じてから再度お試しください。"
              className="mb-3"
            />
            <Button
              label="強制チェックアウト解除"
              icon="pi pi-unlock"
              className="p-button-warning p-button-sm mb-3"
              onClick={excelWriteDialog.forceReleaseCheckout}
              loading={excelWriteDialog.isForceReleasing}
              disabled={excelWriteDialog.isWriting}
            />
          </>
        )}
        <div className="mb-3">
          {excelWriteDialog.progress !== null ? (
            <ProgressBar value={Math.max(0, Math.min(100, excelWriteDialog.progress))} showValue={true} />
          ) : (
            <ProgressBar mode="indeterminate" />
          )}
          <small className="block mt-2 text-600">{excelWriteDialog.lastLog ?? 'ログを待機しています...'}</small>
        </div>
        {excelWriteDialog.isSuccess && (
          <>
            <Message severity="success" text="Excelファイルの書き込みが完了しました" className="mt-3" />
            <p className="text-sm text-600 mt-2">
              計算結果を確定するため、Excelを開いて反映内容を確認してください。
            </p>
          </>
        )}
        {excelWriteDialog.error && (
          <Message severity="error" text={excelWriteDialog.error} className="mt-3" />
        )}
      </Dialog>

      <XMLExportDialog
        visible={xmlModalVisible}
        onHide={() => setXmlModalVisible(false)}
        xmlResponse={xmlResponse}
        onUserChangesApplied={(userChanges: typeof userProductionChanges) => {
          setUserProductionChanges(userChanges)
        }}
      />

      <Toast ref={toastRef} />
    </>
  )
}

export default Plan
